/**
 * RichTextEditor — the corporate dashboard's rich-text editor
 * (corp-dashboard/components/rich-text-editor.tsx), for every Admin HTML
 * body (account alerts, appointment confirmations, email and note templates,
 * the email header and footer, e-form content, lab slides), so they look and
 * behave as corp's do: Visual / Source, block format, font, size, bold /
 * italic / underline / strike, text and highlight colour, lists, alignment,
 * quote, rule, link, table, image, undo / redo, and Insert Shortcode where
 * the body takes them.
 *
 * It edits a contentEditable with execCommand, as corp's does, so pasted or
 * hand-written email HTML (layout tables, inline styles, <center>) shows as
 * it will send. <font> tags become styled <span>s, white "highlights" (a
 * macOS paste artifact) are stripped, and pasted or dropped <style> blocks
 * are flattened into inline styles (inlineHtmlStyles) so they cannot restyle
 * the rest of the page.
 *
 * Unlike corp's: each editor has its own file input — corp's shared id made a
 * second editor's Image button upload into the first.
 *
 * Inside a locked FieldLockScope (a Locked Admin form) it is the content
 * alone: not editable, no toolbar, and loading it never reports a change.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Braces,
  Code2,
  Highlighter,
  Image as ImageIcon,
  Italic,
  Link2,
  List,
  ListOrdered,
  Minus,
  Quote,
  Redo,
  Strikethrough,
  Table2,
  Type,
  UnderlineIcon,
  Undo,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { useFieldLocked } from '@/components/ui/FieldLock';
import { ALERT_TITLE_FONTS } from '@/lib/alertHeroStyle';
import { normalizeHtmlStyles, type NormalizedHtml } from '@/lib/inlineHtmlStyles';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import { EDITOR_IMAGE_MAX_BYTES, uploadEditorImage } from '@/services/editorImages.service';
import { toast } from '@/store/toastStore';
import { fieldClasses } from './DialogChrome';
import { ShortcodeDialog } from './ShortcodeDialog';

// One font list across the announcement editors (the alert title offers the same faces).
const FONT_LIST = ALERT_TITLE_FONTS.filter((f) => f.css).map((f) => ({ label: f.label as string, value: f.css as string }));
const FONT_SIZES = ['1', '2', '3', '4', '5', '6', '7'];
const FONT_SIZE_LABELS: Record<string, string> = { '1': '8pt', '2': '10pt', '3': '12pt', '4': '14pt', '5': '18pt', '6': '24pt', '7': '36pt' };
const BLOCK_FORMATS = [
  { value: 'p', label: 'Normal' },
  { value: 'h1', label: 'Heading 1' },
  { value: 'h2', label: 'Heading 2' },
  { value: 'h3', label: 'Heading 3' },
  { value: 'h4', label: 'Heading 4' },
  { value: 'pre', label: 'Preformatted' },
];
const FONT_TAG_COMMANDS = new Set(['fontName', 'fontSize', 'foreColor']);

function prettifyHtml(html: string): string {
  const tab = '  ';
  let result = '';
  let indent = 0;
  for (const raw of html.replace(/>\s*</g, '>\n<').split('\n')) {
    const token = raw.trim();
    if (!token) continue;
    if (token.startsWith('</')) indent = Math.max(0, indent - 1);
    result += tab.repeat(indent) + token + '\n';
    if (token.match(/^<[a-z][^/]*[^/]>$/i) && !token.match(/^<(br|hr|img|input|meta|link|area|base|col|embed|source|track|wbr)\b/i)) indent++;
  }
  return result.trim();
}

/** Prettified (or pasted) source back to one line: a break inside text or a tag stays a space. */
function minifyHtml(html: string): string {
  return html.replace(/>\s+</g, '><').replace(/\s*\n\s*/g, ' ').trim();
}

/** <font> to styled <span>; white backgrounds (a macOS paste artifact) dropped. */
function sanitizeEditorHtml(container: HTMLElement) {
  container.querySelectorAll('font').forEach((font) => {
    const span = document.createElement('span');
    const styles: string[] = [];
    const face = font.getAttribute('face');
    const color = font.getAttribute('color');
    const size = font.getAttribute('size');
    if (face) styles.push(`font-family: ${face}`);
    if (color) styles.push(`color: ${color}`);
    if (size) styles.push(`font-size: ${FONT_SIZE_LABELS[size] || '12pt'}`);
    if (styles.length > 0) span.setAttribute('style', styles.join('; '));
    span.innerHTML = font.innerHTML;
    font.replaceWith(span);
  });
  container.querySelectorAll<HTMLElement>('[style*="background"]').forEach((el) => {
    const bg = el.style.backgroundColor?.replace(/\s/g, '');
    if (bg === 'white' || bg === 'rgb(255,255,255)' || bg === '#ffffff' || bg === '#fff') {
      el.style.removeProperty('background-color');
      if (!el.getAttribute('style')?.trim()) el.removeAttribute('style');
    }
  });
}

/** What happened to the css that came in with a paste. */
function reportNormalized(result: NormalizedHtml) {
  if (!result.inlined && !result.dropped) return;
  const parts: string[] = [];
  if (result.inlined) parts.push(`${result.inlined} style rule${result.inlined === 1 ? '' : 's'} converted to inline styles`);
  if (result.dropped) parts.push(`${result.dropped} rule${result.dropped === 1 ? '' : 's'} removed — media queries, hover states and the like cannot be inlined`);
  toast.info(`Pasted styles converted: ${parts.join('; ')}.`);
}

export interface RichTextEditorProps {
  id?: string;
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  /** A red border: the form's required-content check failed. */
  invalid?: boolean;
  /** Corp's Insert Shortcode button (the `${PatientFirst}` codes the senders replace). */
  shortcodes?: boolean;
}

export function RichTextEditor({ id, value, onChange, placeholder, invalid = false, shortcodes = false }: RichTextEditorProps) {
  const locked = useFieldLocked();
  const editorRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const isInitialized = useRef(false);
  const pendingHtml = useRef<string | null>(null);
  const lastPropContent = useRef(value);
  const [mode, setMode] = useState<'visual' | 'source'>('visual');
  const [sourceHtml, setSourceHtml] = useState(value);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [shortcodeOpen, setShortcodeOpen] = useState(false);
  // The link and shortcode dialogs take focus and collapse the selection: saved on open, restored to insert.
  const savedRange = useRef<Range | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [, forceUpdate] = useState(0);

  const onChangeRef = useRef(onChange);
  // Locked: tidying the stored HTML on load must not count as an edit.
  onChangeRef.current = locked ? () => {} : onChange;

  /** Loads content into the visual editor, flattening any stored <style> block (and reporting the repair). */
  const applyContent = useCallback((raw: string): string => {
    const normalized = normalizeHtmlStyles(raw);
    if (!editorRef.current) return normalized.html;
    editorRef.current.innerHTML = normalized.html;
    sanitizeEditorHtml(editorRef.current);
    const cleaned = editorRef.current.innerHTML;
    setSourceHtml(cleaned);
    if (normalized.changed) onChangeRef.current(cleaned);
    return cleaned;
  }, []);

  useEffect(() => {
    if (!editorRef.current) return;
    if (pendingHtml.current !== null) {
      applyContent(pendingHtml.current);
      pendingHtml.current = null;
      isInitialized.current = true;
      return;
    }
    if (!isInitialized.current) {
      lastPropContent.current = applyContent(value);
      isInitialized.current = true;
      return;
    }
    // A change from outside (Revert): push it in.
    if (value !== lastPropContent.current) lastPropContent.current = applyContent(value);
  }, [value, mode, applyContent]);

  const emit = useCallback(() => {
    const html = editorRef.current?.innerHTML ?? '';
    lastPropContent.current = html;
    onChange(html);
  }, [onChange]);

  const exec = useCallback(
    (command: string, arg?: string) => {
      editorRef.current?.focus();
      document.execCommand(command, false, arg);
      if (FONT_TAG_COMMANDS.has(command) && editorRef.current) sanitizeEditorHtml(editorRef.current);
      emit();
      forceUpdate((n) => n + 1);
    },
    [emit],
  );

  function handlePaste(e: React.ClipboardEvent) {
    const html = e.clipboardData.getData('text/html');
    if (!html || !editorRef.current) return;
    e.preventDefault();
    // A pasted <style> block would apply to the whole page: flatten it first.
    const normalized = normalizeHtmlStyles(html);
    document.execCommand('insertHTML', false, normalized.html);
    sanitizeEditorHtml(editorRef.current);
    emit();
    reportNormalized(normalized);
  }

  /** Dropped html never reaches the paste handler: let it land, then flatten it. */
  function handleDrop() {
    window.setTimeout(() => {
      if (!editorRef.current) return;
      const normalized = normalizeHtmlStyles(editorRef.current.innerHTML);
      if (normalized.changed) editorRef.current.innerHTML = normalized.html;
      sanitizeEditorHtml(editorRef.current);
      emit();
      if (normalized.changed) reportNormalized(normalized);
    }, 0);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!(e.ctrlKey || e.metaKey)) return;
    const key = e.key.toLowerCase();
    if (key === 'b' || key === 'i' || key === 'u') {
      e.preventDefault();
      exec(key === 'b' ? 'bold' : key === 'i' ? 'italic' : 'underline');
    } else if (key === 'z') {
      e.preventDefault();
      exec(e.shiftKey ? 'redo' : 'undo');
    }
  }

  function saveSelection() {
    const sel = window.getSelection();
    savedRange.current =
      sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.getRangeAt(0).commonAncestorContainer) ? sel.getRangeAt(0).cloneRange() : null;
  }

  function restoreSelection() {
    editorRef.current?.focus();
    const selection = window.getSelection();
    if (savedRange.current && selection) {
      selection.removeAllRanges();
      selection.addRange(savedRange.current);
    }
    savedRange.current = null;
  }

  function openLink() {
    saveSelection();
    const anchor = window.getSelection()?.anchorNode?.parentElement;
    setLinkUrl(anchor?.tagName === 'A' ? (anchor as HTMLAnchorElement).href : '');
    setLinkOpen(true);
  }

  function insertLink() {
    restoreSelection();
    const selection = window.getSelection();
    if (!linkUrl) {
      exec('unlink');
    } else if (!selection || selection.isCollapsed) {
      // The caret inside a link: change its href; otherwise insert the URL as its own text.
      let node: Node | null = selection?.anchorNode ?? null;
      let anchor: HTMLAnchorElement | null = null;
      while (node && node !== editorRef.current) {
        if (node.nodeType === 1 && (node as HTMLElement).tagName === 'A') {
          anchor = node as HTMLAnchorElement;
          break;
        }
        node = node.parentNode;
      }
      if (anchor) {
        anchor.href = linkUrl;
        emit();
      } else {
        const safe = linkUrl.replace(/"/g, '&quot;').replace(/</g, '&lt;');
        exec('insertHTML', `<a href="${safe}">${safe}</a>`);
      }
    } else {
      exec('createLink', linkUrl);
    }
    setLinkOpen(false);
    setLinkUrl('');
  }

  function insertShortcode(code: string) {
    restoreSelection();
    exec('insertHTML', `${code} `);
    setShortcodeOpen(false);
  }

  function blockquoteAncestor(): HTMLElement | null {
    let node: Node | null = window.getSelection()?.anchorNode ?? null;
    while (node && node !== editorRef.current) {
      if (node.nodeType === 1 && (node as HTMLElement).tagName === 'BLOCKQUOTE') return node as HTMLElement;
      node = node.parentNode;
    }
    return null;
  }

  // formatBlock only wraps: a second click unwraps the quote by hand.
  function toggleBlockquote() {
    editorRef.current?.focus();
    const quote = blockquoteAncestor();
    if (quote) {
      quote.replaceWith(...Array.from(quote.childNodes));
      emit();
      forceUpdate((n) => n + 1);
    } else {
      exec('formatBlock', 'blockquote');
    }
  }

  function removeHighlight() {
    editorRef.current?.focus();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !editorRef.current) return;
    const range = selection.getRangeAt(0);
    const clear = (el: HTMLElement) => {
      el.style.removeProperty('background-color');
      if (!el.getAttribute('style')?.trim()) el.removeAttribute('style');
      if (!el.hasAttributes() && el.tagName === 'SPAN') el.replaceWith(...Array.from(el.childNodes));
    };
    if (range.collapsed) {
      // No selection: the nearest highlighted ancestor of the caret.
      let node: Node | null = range.startContainer;
      while (node && node !== editorRef.current) {
        if (node.nodeType === 1 && (node as HTMLElement).style.backgroundColor && (node as HTMLElement).style.backgroundColor !== 'transparent') {
          clear(node as HTMLElement);
          break;
        }
        node = node.parentNode;
      }
    } else {
      const container = range.commonAncestorContainer;
      const root = container.nodeType === 1 ? (container as HTMLElement) : container.parentElement;
      if (root) {
        root.querySelectorAll<HTMLElement>('[style*="background"]').forEach((el) => range.intersectsNode(el) && clear(el));
        if (root.style?.backgroundColor) {
          root.style.removeProperty('background-color');
          if (!root.getAttribute('style')?.trim()) root.removeAttribute('style');
        }
      }
    }
    emit();
    forceUpdate((n) => n + 1);
  }

  async function handleImage(file: File) {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file.');
      return;
    }
    if (file.size > EDITOR_IMAGE_MAX_BYTES) {
      toast.error('Image must be smaller than 10MB.');
      return;
    }
    setUploadingImage(true);
    try {
      const url = await uploadEditorImage(file);
      exec('insertHTML', `<img src="${url.replace(/"/g, '&quot;')}" style="max-width:100%;height:auto;" />`);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The image could not be uploaded.'));
    } finally {
      setUploadingImage(false);
    }
  }

  function insertTable() {
    const th = 'border:1px solid #ccc;padding:8px;background:#f3f4f6';
    const td = 'border:1px solid #ccc;padding:8px';
    const row = `<tr><td style="${td}">&nbsp;</td><td style="${td}">&nbsp;</td><td style="${td}">&nbsp;</td></tr>`;
    exec(
      'insertHTML',
      `<table border="1" cellpadding="5" cellspacing="0" style="border-collapse:collapse;width:100%"><tr><th style="${th}">Header 1</th><th style="${th}">Header 2</th><th style="${th}">Header 3</th></tr>${row}${row}</table><p>&nbsp;</p>`,
    );
  }

  function switchToSource() {
    setSourceHtml(prettifyHtml(editorRef.current?.innerHTML ?? ''));
    setMode('source');
  }

  /** Source is raw: flattened once the field is left, not while typing. */
  function normalizeSource(report: boolean): string {
    const compact = minifyHtml(sourceHtml);
    const normalized = normalizeHtmlStyles(compact);
    if (!normalized.changed) return compact;
    setSourceHtml(prettifyHtml(normalized.html));
    onChange(normalized.html);
    if (report) reportNormalized(normalized);
    return normalized.html;
  }

  function switchToVisual() {
    const compact = normalizeSource(false);
    pendingHtml.current = compact;
    onChange(compact);
    isInitialized.current = false;
    setMode('visual');
  }

  const active = (command: string): boolean => {
    try {
      return document.queryCommandState?.(command) ?? false;
    } catch {
      return false;
    }
  };

  const modeToggle = (
    <div className="mr-1 flex items-center rounded-md bg-slate-200 p-0.5">
      <button
        type="button"
        onClick={mode === 'source' ? switchToVisual : undefined}
        className={cn('rounded px-3 py-1 text-xs font-medium', mode === 'visual' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:bg-slate-100')}
      >
        Visual
      </button>
      <button
        type="button"
        onClick={mode === 'visual' ? switchToSource : undefined}
        className={cn('rounded px-3 py-1 text-xs font-medium', mode === 'source' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:bg-slate-100')}
      >
        {mode === 'visual' && <Code2 className="mr-1 inline h-3 w-3" aria-hidden />}
        Source
      </button>
    </div>
  );

  if (locked) {
    return (
      <div className="rounded-lg border border-slate-200">
        <div
          id={id}
          ref={editorRef}
          contentEditable={false}
          role="textbox"
          aria-multiline="true"
          aria-readonly="true"
          className="rte-content min-h-[200px] p-4 text-sm"
          style={{ wordBreak: 'break-word' }}
        />
      </div>
    );
  }

  const frame = cn('rounded-lg border border-slate-200', invalid && fieldClasses.input('error'));

  if (mode === 'source') {
    return (
      <div className={frame}>
        <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 p-2">
          {modeToggle}
          <span className="text-[10px] text-slate-400">Raw HTML — a &lt;style&gt; block is converted to inline styles when you leave this field</span>
        </div>
        <Textarea
          id={id}
          aria-label="HTML source"
          className="min-h-[300px] resize-none rounded-none rounded-b-lg border-0 font-mono text-xs focus:ring-0"
          value={sourceHtml}
          onChange={(e) => {
            setSourceHtml(e.target.value);
            onChange(minifyHtml(e.target.value));
          }}
          onBlur={() => normalizeSource(true)}
          spellCheck={false}
        />
      </div>
    );
  }

  return (
    <>
      <div className={frame}>
        <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 p-2">
          {modeToggle}
          <Divider />
          <Select<string> wrapperClassName="w-28" className="h-8 py-0 text-xs" options={BLOCK_FORMATS} value="p" onChange={(v) => v && exec('formatBlock', v)} />
          <Select<string>
            wrapperClassName="w-[130px]"
            className="h-8 py-0 text-xs"
            options={[{ value: 'default', label: 'Default font' }, ...FONT_LIST]}
            renderOption={(o) => <span style={o.value !== 'default' ? { fontFamily: o.value } : undefined}>{o.label}</span>}
            value="default"
            onChange={(v) => v && v !== 'default' && exec('fontName', v)}
          />
          <Select<string>
            wrapperClassName="w-20"
            className="h-8 py-0 text-xs"
            options={FONT_SIZES.map((s) => ({ value: s, label: FONT_SIZE_LABELS[s] }))}
            value="3"
            onChange={(v) => v && exec('fontSize', v)}
          />
          <Divider />
          <ToolButton label="Bold" active={active('bold')} onClick={() => exec('bold')}>
            <Bold className="h-4 w-4" />
          </ToolButton>
          <ToolButton label="Italic" active={active('italic')} onClick={() => exec('italic')}>
            <Italic className="h-4 w-4" />
          </ToolButton>
          <ToolButton label="Underline" active={active('underline')} onClick={() => exec('underline')}>
            <UnderlineIcon className="h-4 w-4" />
          </ToolButton>
          <ToolButton label="Strikethrough" active={active('strikeThrough')} onClick={() => exec('strikeThrough')}>
            <Strikethrough className="h-4 w-4" />
          </ToolButton>
          <Divider />
          <div className="flex items-center gap-1">
            <Type className="h-4 w-4 text-slate-500" aria-hidden />
            <input
              type="color"
              defaultValue="#000000"
              onChange={(e) => exec('foreColor', e.target.value)}
              className="h-8 w-12 cursor-pointer rounded-md border border-slate-200 p-1"
              title="Text Color"
              aria-label="Text Color"
            />
          </div>
          <div className="flex items-center gap-1">
            <Highlighter className="h-4 w-4 text-slate-500" aria-hidden />
            <input
              type="color"
              defaultValue="#ffff00"
              onChange={(e) => exec('hiliteColor', e.target.value)}
              className="h-8 w-12 cursor-pointer rounded-md border border-slate-200 p-1"
              title="Highlight Color"
              aria-label="Highlight Color"
            />
            <ToolButton label="Remove Highlight" onClick={removeHighlight} compact>
              <X className="h-3.5 w-3.5" />
            </ToolButton>
          </div>
          <Divider />
          <ToolButton label="Bulleted list" onClick={() => exec('insertUnorderedList')}>
            <List className="h-4 w-4" />
          </ToolButton>
          <ToolButton label="Numbered list" onClick={() => exec('insertOrderedList')}>
            <ListOrdered className="h-4 w-4" />
          </ToolButton>
          <Divider />
          <ToolButton label="Align left" onClick={() => exec('justifyLeft')}>
            <AlignLeft className="h-4 w-4" />
          </ToolButton>
          <ToolButton label="Align center" onClick={() => exec('justifyCenter')}>
            <AlignCenter className="h-4 w-4" />
          </ToolButton>
          <ToolButton label="Align right" onClick={() => exec('justifyRight')}>
            <AlignRight className="h-4 w-4" />
          </ToolButton>
          <ToolButton label="Justify" onClick={() => exec('justifyFull')}>
            <AlignJustify className="h-4 w-4" />
          </ToolButton>
          <Divider />
          <ToolButton label="Blockquote" active={!!blockquoteAncestor()} onClick={toggleBlockquote}>
            <Quote className="h-4 w-4" />
          </ToolButton>
          <ToolButton label="Horizontal rule" onClick={() => exec('insertHorizontalRule')}>
            <Minus className="h-4 w-4" />
          </ToolButton>
          <ToolButton label="Link" onClick={openLink}>
            <Link2 className="h-4 w-4" />
          </ToolButton>
          <Divider />
          <ToolButton label="Insert Table" onClick={insertTable}>
            <Table2 className="h-4 w-4" />
          </ToolButton>
          <Divider />
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            disabled={uploadingImage}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleImage(file);
              e.target.value = '';
            }}
          />
          <ToolButton label="Insert Image" disabled={uploadingImage} onClick={() => fileRef.current?.click()}>
            <ImageIcon className="h-4 w-4" />
          </ToolButton>
          <Divider />
          <ToolButton label="Undo" onClick={() => exec('undo')}>
            <Undo className="h-4 w-4" />
          </ToolButton>
          <ToolButton label="Redo" onClick={() => exec('redo')}>
            <Redo className="h-4 w-4" />
          </ToolButton>
          {shortcodes && (
            <>
              <Divider />
              <ToolButton
                label="Insert Shortcode"
                onClick={() => {
                  saveSelection();
                  setShortcodeOpen(true);
                }}
              >
                <Braces className="h-4 w-4" />
              </ToolButton>
            </>
          )}
        </div>
        <div
          id={id}
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          onInput={emit}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onDrop={handleDrop}
          className="rte-content min-h-[200px] p-4 text-sm focus:outline-none empty:before:pointer-events-none empty:before:text-slate-400 empty:before:content-[attr(data-placeholder)]"
          style={{ wordBreak: 'break-word' }}
          data-placeholder={placeholder}
        />
      </div>

      <Modal
        isOpen={linkOpen}
        onClose={() => setLinkOpen(false)}
        title="Insert Link"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setLinkOpen(false)}>
              Cancel
            </Button>
            <Button onClick={insertLink}>Insert Link</Button>
          </>
        }
      >
        <div className="space-y-2">
          <label htmlFor={`${id ?? 'rte'}-link-url`} className="text-sm font-medium text-slate-900">
            URL
          </label>
          <Input
            id={`${id ?? 'rte'}-link-url`}
            placeholder="https://example.com"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                insertLink();
              }
            }}
          />
        </div>
      </Modal>

      {shortcodeOpen && <ShortcodeDialog onClose={() => setShortcodeOpen(false)} onInsert={insertShortcode} />}
    </>
  );
}

function Divider() {
  return <div className="mx-1 h-6 w-px bg-slate-300" aria-hidden />;
}

/** Corp's ghost "sm" icon button. */
function ToolButton({
  label,
  active = false,
  disabled = false,
  compact = false,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  compact?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      aria-pressed={active || undefined}
      className={cn(
        'inline-flex h-8 items-center justify-center rounded-md text-slate-700 transition-colors hover:bg-slate-100 disabled:opacity-50',
        compact ? 'w-8' : 'px-2.5',
        active && 'bg-slate-200',
      )}
    >
      {children}
    </button>
  );
}
